import React, {useEffect, useState} from 'react';
import {
    AuthorizationRecordTypeMap,
    AuthorizationStatusMapWithDefault, AuthorizationTypeMap,
    ClientReportReportStatusMapWithDefault, CurrentAcitveAuthorizationServiceTypeFilterOption, FunderAuthorizationRecordTypeMap,
} from "../domain";
import {AuthorizationListAction, AuthorizationListFilterValue} from "./authorizationListReducer";
import DatePicker from "react-datepicker";
import {DatePickerInput} from "../../../common/component/DatePickerInput";
import {DropdownMultiSelect, MultiFilterButton, SimpleDropdown} from "common";
import {tryFormatLocalDate} from "../../../common/util/date";
import {add} from "date-fns";
import { SPLIT_DELIMITER } from 'placement/const';

export type AuthorizationListFilterParams = {
    recordType: string | null;
    faRecordType: string | null;
    serviceTypes: Array<string>;
    status: string | null;
    reportStatus: string | null;
    reportSubmittedDateFrom: string | null;
    reportSubmittedDateTo: string | null;
    reportDueDateFrom: string | null;
    reportDueDateTo: string | null;
}

export function asQueryParam(filter: AuthorizationListFilterValue | undefined): AuthorizationListFilterParams {
    let authType = filter?.authType ?? "";
    return {
        reportStatus: filter?.reportStatus ? filter?.reportStatus : null,
        reportDueDateFrom: tryFormatLocalDate(filter?.reportDueDateFrom ?? null),
        reportDueDateTo: tryFormatLocalDate(filter?.reportDueDateTo ?? null),
        reportSubmittedDateFrom: tryFormatLocalDate(filter?.reportSubmittedDateFrom ?? null),
        reportSubmittedDateTo: tryFormatLocalDate(filter?.reportSubmittedDateTo ?? null),
        recordType: authType === "" || !AuthorizationRecordTypeMap.has(authType) ? null : authType,
        faRecordType: authType === "" || !FunderAuthorizationRecordTypeMap.has(authType) ? null : authType,
        serviceTypes: filter?.serviceType ? filter?.serviceType
            .split(SPLIT_DELIMITER)
            .filter(
                (serviceType) => serviceType !== "") : [],
        status: filter?.status ? filter?.status : null,
    };
}

type Props = {
    initFilterValue: AuthorizationListFilterValue,
    dispatch: React.Dispatch<AuthorizationListAction>
}

export function AuthorizationListFilter({initFilterValue, dispatch}: Props) {
    const [filterValue, setFilterValue] = useState({...initFilterValue});
    const currentDate: Date = new Date();
    // update internal value when init filter value updated
    useEffect(() => {
        setFilterValue(initFilterValue);
    }, [initFilterValue])

    const onFilterUpdate = (fieldName: string, fieldValue: any) => {
        setFilterValue({...filterValue, [fieldName]: fieldValue})
    }

    return <MultiFilterButton<AuthorizationListFilterValue>
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        dispatchSetFilterValue={(val) => dispatch({type: "SET_FILTER", filter: val})}
        filterOptions={[
            {
                label: "Type",
                component: <SimpleDropdown
                    options={AuthorizationTypeMap}
                    selected={filterValue.authType}
                    minWidth={"7rem"}
                    textColor={"gray-600"}
                    setSelected={(val) => onFilterUpdate("authType", val)}/>
            },
            {
                label: "Service Type",
                component: <DropdownMultiSelect field='serviceType'
                                               options={Array.from(CurrentAcitveAuthorizationServiceTypeFilterOption.keys())}
                                               labelMapperFunc={(val) => CurrentAcitveAuthorizationServiceTypeFilterOption.get(val)!}
                                               full
                                               value={filterValue.serviceType}
                                               setValue={(values) => onFilterUpdate("serviceType", values)}/>
            },
            {
                label: "Status",
                component: <SimpleDropdown
                    options={AuthorizationStatusMapWithDefault}
                    selected={filterValue.status}
                    minWidth={"7rem"}
                    textColor={"gray-600"}
                    setSelected={(val) => onFilterUpdate("status", val)}/>
            },
            {
                label: "Report Due Date From",
                component: <DatePicker
                    wrapperClassName="w-full"
                    selected={filterValue.reportDueDateFrom || null}
                    onSelect={(newValue: Date | null) => onFilterUpdate("reportDueDateFrom", newValue)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="reportDueDateFrom"
                        value={undefined}
                        onFinish={(newValue: Date | null) => onFilterUpdate("reportDueDateFrom", newValue)}
                    />}
                />
            },
            {
                label: "Report Due Date To",
                component: <DatePicker
                    wrapperClassName="w-full"
                    selected={filterValue.reportDueDateTo || null}
                    onSelect={(newValue: Date | null) => onFilterUpdate("reportDueDateTo", newValue)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="reportDueDateTo"
                        value={undefined}
                        onFinish={(newValue: Date | null) => onFilterUpdate("reportDueDateTo", newValue)}
                    />}
                />
            },
            {
                label: "Report Status",
                component: <SimpleDropdown
                    options={ClientReportReportStatusMapWithDefault}
                    selected={filterValue.reportStatus}
                    minWidth={"7rem"}
                    textColor={"gray-600"}
                    setSelected={(val) => onFilterUpdate("reportStatus", val)}/>
            },
            {
                label: "Report Submitted Date From",
                component:
                    <DatePicker
                        wrapperClassName="w-full"
                        selected={filterValue.reportSubmittedDateFrom || null}
                        onSelect={(newValue: Date | null) => onFilterUpdate("reportSubmittedDateFrom", newValue)}
                        onChange={() => {}}
                        customInput={<DatePickerInput
                            fieldName="reportSubmittedDateFrom"
                            value={undefined}
                            onFinish={(newValue: Date | null) => onFilterUpdate("reportSubmittedDateFrom", newValue)}
                        />}
                    />
            },
            {
                label: "Report Submitted Date To",
                component: <DatePicker
                    wrapperClassName="w-full"
                    selected={filterValue.reportSubmittedDateTo || null}
                    onSelect={(newValue: Date | null) => onFilterUpdate("reportSubmittedDateTo", newValue)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="reportSubmittedDateTo"
                        value={undefined}
                        onFinish={(newValue: Date | null) => onFilterUpdate("reportSubmittedDateTo", newValue)}
                    />}
                />
            },
        ]}
        emptyFilterValue={{
            authType: "",
            serviceType: "",
            status: "",
            reportSubmittedDateFrom: null,
            reportSubmittedDateTo: null,
            reportDueDateTo: add(currentDate, {days: 45}),
            reportDueDateFrom: add(currentDate, {days: -15}),
            reportStatus: "",
        }}
    />;
}
